import { BudgetItem, BudgetTransaction, BucketNodeItem, CustomReportSettings, GroupBy, Grouping, SubBudgetItem, TransactionItem, UserItem } from "../../types";

export interface BuildPreviewInput {
	transactions: TransactionItem[];
	checkRequests?: TransactionItem[];
	budgets: BudgetItem[];
	subBudgets: SubBudgetItem[];
	users?: UserItem[];
	settings: CustomReportSettings;
}

export interface BuildPreviewResult {
	groups: Grouping[];
	grandTotal: number;
}

const buildBucketsForAllocation = (bucketNodes: BucketNodeItem[] | undefined): { [bucketTypeKey: string]: BucketNodeItem[] } => {
	const result: { [bucketTypeKey: string]: BucketNodeItem[] } = {};
	(bucketNodes ?? []).forEach(node => {
		if (!node.bucketTypeKey) return;
		const key = node.bucketTypeKey;
		if (!result[key]) result[key] = [];
		result[key].push(node);
	});
	return result;
};

const matchesBucketFilter = (
	bucketNodes: BucketNodeItem[] | undefined,
	filter: { [bucketTypeKey: string]: number[] } | undefined
): boolean => {
	if (!filter) return true;
	const keys = Object.keys(filter);
	if (keys.length === 0) return true;
	const nodes = bucketNodes ?? [];
	return keys.every(typeKey => {
		const ids = filter[typeKey];
		if (!ids || ids.length === 0) return true;
		return nodes.some(node => ids.includes(node.bucketNodeId)
			&& node.bucketTypeKey?.toLowerCase() === typeKey.toLowerCase());
	});
};

export const buildReportPreview = (input: BuildPreviewInput): BuildPreviewResult => {
	const { transactions, checkRequests, budgets, subBudgets, users, settings } = input;
	const { filter, grouping } = settings;

	let source: TransactionItem[] = transactions || [];
	if (filter.includeCheckRequests && checkRequests) {
		source = [...source, ...checkRequests];
	}

	// Filter by date and type (spending/miles)
	let filtered = source.filter(t =>
		(!filter.startDate || t.transactionDate >= filter.startDate.startOfDay()) &&
		(!filter.endDate || t.transactionDate <= filter.endDate.startOfDay()) &&
		(!filter.includeMiles || (t.miles && t.miles > 0)) &&
		(!filter.includeSpending || !(t.miles && t.miles > 0))
	);

	// Filter users by selected users
	if (filter.createdById && filter.createdById.length && users && users.length) {
		const selectedUserNames = users.filter(u => filter.createdById?.includes(u.userAccountId)).map(u => `${u.firstName} ${u.lastName}`);
		filtered = filtered.filter(t => selectedUserNames.includes(t.created));
	}

	// Convert to BudgetTransaction[] applying selected filters
	const records: (BudgetTransaction & { User?: UserItem })[] = [];
	filtered.forEach(transaction => {
		transaction.allocations.forEach(amount => {
			if (filter.budgetIds && filter.budgetIds.length && !filter.budgetIds.includes(amount.budgetId)) return;
			if (filter.subBudgetIds && filter.subBudgetIds.length && !filter.subBudgetIds.includes(amount.subBudgetId ?? -5)) return;
			if (!matchesBucketFilter(amount.bucketNodes, filter.bucketNodeIds)) return;
			const budget = budgets?.find(b => b.budgetId === amount.budgetId) as BudgetItem | undefined;
			const subBudget = subBudgets?.find(sb => sb.subBudgetId === amount.subBudgetId) as SubBudgetItem | undefined;
			const record: any = {
				Budget: budget,
				SubBudget: subBudget,
				Buckets: buildBucketsForAllocation(amount.bucketNodes),
				Transaction: transaction,
				Amount: amount,
				User: users?.find(u => `${u.firstName} ${u.lastName}` === transaction.created),
				Date: transaction.transactionDate.toString(),
				Merchant: transaction.merchant
			} as BudgetTransaction & { User?: UserItem };
			records.push(record);
		});
	});

	// Group or return all
	let groups: Grouping[];
	if (grouping && grouping.toLowerCase().startsWith('bucket:')) {
		const bucketTypeKey = grouping.substring('bucket:'.length);
		groups = records.reduce((result: Grouping[], item: any) => {
			const nodes = (item.Buckets?.[bucketTypeKey] ?? []) as BucketNodeItem[];
			const key = nodes.length > 0
				? nodes.map(n => n.displayName).filter(Boolean).join(', ')
				: '';
			let res = result.find(r => r.Key === key);
			if (res) res.Value.push(item);
			else result.push({ Key: key, Value: [item] } as Grouping);
			return result;
		}, []);
	} else if (grouping) {
		groups = GroupBy(records, grouping);
	} else {
		groups = [{ Key: undefined, Value: records } as Grouping];
	}

	const grandTotal = records.reduce((tot, r) => tot + r.Amount.amount, 0);
	return { groups, grandTotal };
};
